import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, Trash2, Edit, Plus, X, Save } from 'lucide-react';
import BottomNavigation from '../../components/common/BottomNavigation';
import { useApp } from '../../context/AppContext';
import { useData } from '../../context/DataContext';
import { Table } from '../../types';
import { generateShortId } from '../../utils/idGenerator';

const AdminTable: React.FC = () => {
  const { setCurrentScreen } = useApp();
  const { tables, updateTables } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingTable, setEditingTable] = useState<Table | null>(null);
  const [deletingTableId, setDeletingTableId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    tableNumber: '',
    capacity: 4,
    location: '',
    status: 'available' as 'available' | 'occupied' | 'reserved'
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleBack = () => {
    setCurrentScreen('admin-dashboard');
  };

  const resetForm = () => {
    setFormData({
      tableNumber: '',
      capacity: 4,
      location: '',
      status: 'available'
    });
  };

  const handleAddTable = () => {
    resetForm();
    setShowAddModal(true);
  };

  const handleEditTable = (table: Table) => {
    setEditingTable(table);
    setFormData({
      tableNumber: table.tableNumber,
      capacity: table.capacity,
      location: table.location,
      status: table.status
    });
    setShowEditModal(true);
  };

  const handleDeleteTable = (id: string) => {
    setDeletingTableId(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deletingTableId) return;
    
    setIsLoading(true);
    try {
      const updatedTables = tables.filter(t => t.id !== deletingTableId);
      await updateTables(updatedTables);
      setShowDeleteConfirm(false);
      setDeletingTableId(null);
    } catch (error) {
      console.error('Error deleting table:', error);
      alert('Error deleting table. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitAdd = async () => {
    if (!formData.tableNumber.trim() || !formData.location.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const newTable: Table = {
        id: generateShortId(),
        tableNumber: formData.tableNumber.trim(),
        capacity: formData.capacity,
        location: formData.location.trim(),
        status: formData.status
      };

      console.log('🪑 AdminTable: Adding new table:', newTable);
      const updatedTables = [...tables, newTable];
      await updateTables(updatedTables);
      console.log('✅ AdminTable: Table added successfully');
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('❌ AdminTable: Error adding table:', error);
      alert('Error adding table. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitEdit = async () => {
    if (!formData.tableNumber.trim() || !formData.location.trim() || !editingTable) {
      alert('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const updatedTables = tables.map(t =>
        t.id === editingTable.id
          ? {
              ...t,
              tableNumber: formData.tableNumber.trim(),
              capacity: formData.capacity,
              location: formData.location.trim(),
              status: formData.status
            }
          : t
      );

      await updateTables(updatedTables);
      setShowEditModal(false);
      setEditingTable(null);
      resetForm();
    } catch (error) {
      console.error('Error updating table:', error);
      alert('Error updating table. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTables = tables.filter(t =>
    t.tableNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'bg-green-900 text-green-400';
      case 'occupied':
        return 'bg-red-900 text-red-400';
      case 'reserved':
        return 'bg-yellow-900 text-yellow-400';
      default:
        return 'bg-slate-900 text-slate-400';
    }
  };

  const TableItem: React.FC<{ table: Table; index: number }> = ({ table, index }) => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.1 }}
      className="flex items-center justify-between p-4 bg-slate-800 rounded-lg border border-slate-700"
    >
      <div className="flex items-center space-x-3">
        <div className="w-12 h-12 bg-slate-700 rounded-lg flex items-center justify-center">
          <span className="text-slate-300 font-mono text-xs">🪑</span>
        </div>
        <div>
          <h3 className="text-white font-semibold text-lg">{table.tableNumber}</h3>
          <p className="text-slate-400 text-sm">{table.location} • {table.capacity} seats</p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(table.status)}`}>
          {table.status}
        </span>
        <button 
          onClick={() => handleEditTable(table)}
          className="p-1 text-slate-400 hover:text-white transition-colors"
          disabled={isLoading}
        >
          <Edit size={16} />
        </button>
        <button 
          onClick={() => handleDeleteTable(table.id)}
          className="p-1 text-slate-400 hover:text-red-400 transition-colors"
          disabled={isLoading}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-slate-900 pb-20">
      {/* Header */}
      <div className="flex items-center p-6 bg-slate-800 border-b border-slate-700">
        <button
          onClick={handleBack}
          className="p-2 text-slate-400 hover:text-white mr-4"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-xl font-bold text-white">Table Management</h1>
      </div>

      <div className="p-6 space-y-6">
        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative"
        >
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
            placeholder="Search tables"
          />
        </motion.div>

        {/* Available Tables */}
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">Available Tables</h2>
          <div className="space-y-3">
            {filteredTables.map((table, index) => (
              <TableItem key={table.id} table={table} index={index} />
            ))}
            {filteredTables.length === 0 && (
              <div className="text-center py-8 text-slate-400">
                {searchTerm ? 'No tables found matching your search.' : 'No tables available.'}
              </div>
            )}
          </div>
        </div>

        {/* Add Table Button */}
        <motion.button
          onClick={handleAddTable}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={isLoading}
          className="w-full bg-gradient-to-r from-purple-500 to-teal-500 hover:from-purple-600 hover:to-teal-600 disabled:from-slate-600 disabled:to-slate-600 text-white font-semibold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2"
        >
          <Plus size={20} />
          <span>{isLoading ? 'Processing...' : 'Add Table'}</span>
        </motion.button>
      </div>

      <BottomNavigation />

      {/* Add Table Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-800 rounded-lg p-6 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Add New Table</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 text-slate-400 hover:text-white"
                disabled={isLoading}
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Table Number *
                </label>
                <input
                  type="text"
                  value={formData.tableNumber}
                  onChange={(e) => setFormData({ ...formData, tableNumber: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., T01"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Capacity *
                </label>
                <input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Number of seats"
                  min="1"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Location *
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Main Hall"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'available' | 'occupied' | 'reserved' })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  disabled={isLoading}
                >
                  <option value="available">Available</option>
                  <option value="occupied">Occupied</option>
                  <option value="reserved">Reserved</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAdd}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-teal-500 text-white rounded-lg hover:from-purple-600 hover:to-teal-600 transition-all flex items-center justify-center space-x-2"
                disabled={isLoading}
              >
                <Save size={16} />
                <span>{isLoading ? 'Adding...' : 'Add Table'}</span>
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Table Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-800 rounded-lg p-6 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Edit Table</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingTable(null);
                  resetForm();
                }}
                className="p-1 text-slate-400 hover:text-white"
                disabled={isLoading}
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Table Number *
                </label>
                <input
                  type="text"
                  value={formData.tableNumber}
                  onChange={(e) => setFormData({ ...formData, tableNumber: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., T01"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Capacity *
                </label>
                <input
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Number of seats"
                  min="1"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Location *
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="e.g., Main Hall"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'available' | 'occupied' | 'reserved' })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  disabled={isLoading}
                >
                  <option value="available">Available</option>
                  <option value="occupied">Occupied</option>
                  <option value="reserved">Reserved</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingTable(null);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitEdit}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-teal-500 text-white rounded-lg hover:from-purple-600 hover:to-teal-600 transition-all flex items-center justify-center space-x-2"
                disabled={isLoading}
              >
                <Save size={16} />
                <span>{isLoading ? 'Updating...' : 'Update Table'}</span>
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-800 rounded-lg p-6 w-full max-w-md"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Confirm Delete</h2>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingTableId(null);
                }}
                className="p-1 text-slate-400 hover:text-white"
                disabled={isLoading}
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-slate-300 mb-6">
              Are you sure you want to delete this table? This action cannot be undone.
            </p>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingTableId(null);
                }}
                className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center space-x-2"
                disabled={isLoading}
              >
                <Trash2 size={16} />
                <span>{isLoading ? 'Deleting...' : 'Delete'}</span>
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminTable;
